import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  HStack,
  Text,
  FormControl,
  FormLabel,
  Select,
  Button,
} from "@chakra-ui/react";
import DataTable from "react-data-table-component";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar, faFileExport } from "@fortawesome/free-solid-svg-icons";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// สีตามประเภทปศุสัตว์
const typeColors = {
  POULTRY: "#ef7c8e",
  CATTLE: "#879e82",
  PIG: "#5e87f5",
};

// ฟังก์ชันแปลงเลขเดือนเป็นชื่อเดือนภาษาไทย
const getThaiMonth = (monthNumber) => {
  const thaiMonths = [
    "มกราคม",
    "กุมภาพันธ์",
    "มีนาคม",
    "เมษายน",
    "พฤษภาคม",
    "มิถุนายน",
    "กรกฎาคม",
    "สิงหาคม",
    "กันยายน",
    "ตุลาคม",
    "พฤศจิกายน",
    "ธันวาคม",
  ];
  return thaiMonths[monthNumber - 1] || "";
};

const VaccineDistribution = () => {
  const { sessionId } = useAuth();
  const [receiptData, setReceiptData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [hoveredType, setHoveredType] = useState(null);
  const componentRef = useRef();

// ดึงเดือนปัจจุบัน
  const currentMonth = new Date().getMonth() + 1; // getMonth() คืนค่า 0-11, จึงบวก 1
  const thaiMonthName = getThaiMonth(currentMonth);

  // ดึงข้อมูลจาก API
  useEffect(() => {
    const fetchReceipts = async () => {
      setIsLoading(true);
      try {
        const response = await apiService({
          method: "POST",
          url: "/getVaccineDistributionSummary",
          data: {
            Month: "2025-04",
          },
          sessionId: sessionId || "",
        });
        if (response.result === "success" && response.summary) {
          const groupedData = response.summary.reduce((acc, item) => {
            const key = `${item.LivestockType}_${item.VaccineNameTH}`;
            if (!acc[key]) {
              acc[key] = {
                LivestockType: item.LivestockType,
                VaccineNameTH: item.VaccineNameTH,
                VaccineNameEN: item.VaccineNameEN,
                TotalQuantityFarmerReservation: 0,
                TotalValueFarmerReservation: 0,
                TotalQuantityOfficeOrder: 0,
                TotalValueOfficeOrder: 0,
                TotalQuantitySale: 0,
                TotalValueSale: 0,
              };
            }
            acc[key].TotalQuantityFarmerReservation += Number(item.TotalQuantityFarmerReservation);
            acc[key].TotalValueFarmerReservation += Number(item.TotalValueFarmerReservation);
            acc[key].TotalQuantityOfficeOrder += Number(item.TotalQuantityOfficeOrder);
            acc[key].TotalValueOfficeOrder += Number(item.TotalValueOfficeOrder);
            acc[key].TotalQuantitySale += Number(item.TotalQuantitySale);
            acc[key].TotalValueSale += Number(item.TotalValueSale);
            return acc;
          }, {});
          const receipts = Object.values(groupedData);
          setReceiptData(receipts);

          const summary = receipts.reduce((acc, item) => {
            const type = item.LivestockType;
            if (!acc[type]) {
              acc[type] = {
                LivestockType: type,
                QuantityFarmerReservation: 0,
                ValueFarmerReservation: 0,
                QuantityOfficeOrder: 0,
                ValueOfficeOrder: 0,
                QuantitySale: 0,
                ValueSale: 0,
              };
            }
            acc[type].QuantityFarmerReservation += item.TotalQuantityFarmerReservation;
            acc[type].ValueFarmerReservation += item.TotalValueFarmerReservation;
            acc[type].QuantityOfficeOrder += item.TotalQuantityOfficeOrder;
            acc[type].ValueOfficeOrder += item.TotalValueOfficeOrder;
            acc[type].QuantitySale += item.TotalQuantitySale;
            acc[type].ValueSale += item.TotalValueSale;
            return acc;
          }, {});
          setSummaryData(Object.values(summary));
        } else {
          setReceiptData([]);
          setSummaryData([]);
          console.error("Failed to fetch distribution data:", response.error);
        }
      } catch (error) {
        console.error("Error fetching distribution data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (sessionId) fetchReceipts();
  }, [sessionId]);

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = () => {
    const activeType = hoveredType || selectedType;
    if (activeType) {
      return receiptData
        .filter((item) => item.LivestockType === activeType)
        .map((item) => ({
          name: item.VaccineNameTH,
          farmerReservation: item.TotalQuantityFarmerReservation,
          officeOrder: item.TotalQuantityOfficeOrder,
          sale: item.TotalQuantitySale,
          valueFarmerReservation: item.TotalValueFarmerReservation,
          valueOfficeOrder: item.TotalValueOfficeOrder,
          valueSale: item.TotalValueSale,
          type: item.LivestockType,
        }));
    }
    return summaryData.map((item) => ({
      name: item.LivestockType === "POULTRY" ? "สัตว์ปีก" : item.LivestockType === "CATTLE" ? "โค-กระบือ" : "สุกร",
      farmerReservation: item.QuantityFarmerReservation,
      officeOrder: item.QuantityOfficeOrder,
      sale: item.QuantitySale, // แก้ไขจาก inspectingSale เป็น item.QuantitySale
      valueFarmerReservation: item.ValueFarmerReservation,
      valueOfficeOrder: item.ValueOfficeOrder,
      valueSale: item.ValueSale,
      type: item.LivestockType,
    }));
  };

  // คอลัมน์ตาราง
  const columns = [
    {
      name: "ประเภทปศุสัตว์",
      selector: (row) => row.LivestockType,
      sortable: true,
      cell: (row) => (
        <HStack spacing={2}>
          <Box
            w="8px"
            h="12px"
            bg={typeColors[row.LivestockType]}
            borderRadius="sm"
          />
          <Text>
            {row.LivestockType === "POULTRY" ? "สัตว์ปีก" : row.LivestockType === "CATTLE" ? "โค-กระบือ" : "สุกร"}
          </Text>
        </HStack>
      ),
    },
    {
      name: "ยอดจองเกษตรกร",
      selector: (row) => row.QuantityFarmerReservation,
      sortable: true,
      format: (row) => row.QuantityFarmerReservation.toLocaleString("th-TH"),
    },
    {
      name: "มูลค่า",
      selector: (row) => row.ValueFarmerReservation,
      sortable: true,
      format: (row) => row.ValueFarmerReservation.toLocaleString("th-TH"),
    },
    {
      name: "ยอดจองหน่วยงาน",
      selector: (row) => row.QuantityOfficeOrder,
      sortable: true,
      format: (row) => row.QuantityOfficeOrder.toLocaleString("th-TH"),
    },
    {
      name: "มูลค่า",
      selector: (row) => row.ValueOfficeOrder,
      sortable: true,
      format: (row) => row.ValueOfficeOrder.toLocaleString("th-TH"),
    },
    {
      name: "ยอดจำหน่าย",
      selector: (row) => row.QuantitySale,
      sortable: true,
      format: (row) => row.QuantitySale.toLocaleString("th-TH"),
    },
    {
      name: "มูลค่า",
      selector: (row) => row.ValueSale,
      sortable: true,
      format: (row) => row.ValueSale.toLocaleString("th-TH"),
    },
  ];

  // Tooltip ที่กำหนดเอง
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <Text><strong>ชื่อ:</strong> {label}</Text>
          <Text><strong>จำนวนจองเกษตรกร:</strong> {data.farmerReservation.toLocaleString("th-TH")} โดส</Text>
          <Text><strong>มูลค่าจองเกษตรกร:</strong> {data.valueFarmerReservation.toLocaleString("th-TH")} บาท</Text>
          <Text><strong>จำนวนจองหน่วยงาน:</strong> {data.officeOrder.toLocaleString("th-TH")} โดส</Text>
          <Text><strong>มูลค่าจองหน่วยงาน:</strong> {data.valueOfficeOrder.toLocaleString("th-TH")} บาท</Text>
          <Text><strong>จำนวนจำหน่าย:</strong> {data.sale.toLocaleString("th-TH")} โดส</Text>
          <Text><strong>มูลค่าจำหน่าย:</strong> {data.valueSale.toLocaleString("th-TH")} บาท</Text>
        </Box>
      );
    }
    return null;
  };

  // ฟังก์ชันสำหรับ export เป็น PDF
  const exportToPDF = () => {
    const input = componentRef.current;
    html2canvas(input, {
      scale: 1.5, // ลดสเกลเพื่อลดปัญหาการตัดข้อความ
      useCORS: true,
      scrollY: -window.scrollY, // ปรับตำแหน่ง scroll เพื่อให้เรนเดอร์ครบ
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgProps = pdf.getImageProperties(imgData);
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("vaccine_distribution_report.pdf");
    });
  };

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
      p={5}
      mt={6}
      bg="white"
    >
      <Box
        mb={5}
        borderRadius="full"
        w="100%"
        bgColor="iBlue.500"
        color="white"
        p={2}
      >
        <HStack spacing={5} ml={5} justifyContent="space-between">
          <HStack spacing={5}>
            <FontAwesomeIcon icon={faChartBar} color="white" size="xl" />
            <Heading size={{ base: "md", md: "md" }} fontWeight="normal">
              สรุปการจองและจำหน่ายวัคซีนประจำเดือน {thaiMonthName}
            </Heading>
          </HStack>
          <Button
            className="export-button"
            leftIcon={<FontAwesomeIcon icon={faFileExport} />}
            colorScheme="iBlue"
            variant="solid"
            size="sm"
            mr={5}
            onClick={exportToPDF}
          >
            ส่งออกเป็น PDF
          </Button>
        </HStack>
      </Box>

      {/* เนื้อหาที่ต้องการ export */}
      <Box ref={componentRef}>
        {/* ตาราง */}
        {isLoading ? (
          <Text textAlign="center" color="gray.500" p={5}>
            กำลังโหลดข้อมูล...
          </Text>
        ) : summaryData.length > 0 ? (
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={summaryData}
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
              onRowMouseEnter={(row) => setHoveredType(row.LivestockType)}
              onRowMouseLeave={() => setHoveredType(null)}
            />
          </Box>
        ) : (
          <Text textAlign="center" color="gray.500" p={5}>
            ไม่มีข้อมูลสำหรับแสดง
          </Text>
        )}

        {/* ตัวกรอง */}
        <FormControl maxW="300px" m={5}>
          <FormLabel fontSize="xl" color="gray">
            เลือกประเภทปศุสัตว์
          </FormLabel>
          <Select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            placeholder="ทั้งหมด"
            colorScheme="iBlue"
          >
            <option value="POULTRY">สัตว์ปีก</option>
            <option value="CATTLE">โค-กระบือ</option>
            <option value="PIG">สุกร</option>
          </Select>
        </FormControl>

        {/* กราฟ */}
        {chartData().length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={chartData()}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                interval={0}
                height={100}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                label={{
                  value: "จำนวนวัคซีน (โดส)",
                  angle: -90,
                  position: "insideLeft",
                  offset: -5,
                }}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => value.toLocaleString("th-TH")}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                layout="horizontal"
                verticalAlign="top"
                align="right"
                wrapperStyle={{ paddingBottom: 10 }}
              />
              <Bar
                dataKey="farmerReservation"
                name="จองเกษตรกร"
                stackId="reservation"
                barSize={25}
                fill="#606060"
              >
                {chartData().map((entry, index) => (
                  <Cell key={`farmer-${index}`} fill={typeColors[entry.type]} />
                ))}
              </Bar>
              <Bar
                dataKey="officeOrder"
                name="จองหน่วยงาน"
                stackId="reservation"
                barSize={25}
                fill="#b0b0b0"
              >
                {chartData().map((entry, index) => (
                  <Cell key={`office-${index}`} fill={typeColors[entry.type]} opacity={0.4} />
                ))}
              </Bar>
              <Bar
                dataKey="sale"
                name="จำหน่าย"
                barSize={25}
                fill="#808080"
              >
                {chartData().map((entry, index) => (
                  <Cell key={`sale-${index}`} fill={typeColors[entry.type]} opacity={0.7} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Text textAlign="center" color="gray.500" p={5}>
            ไม่มีข้อมูลสำหรับแสดงกราฟ
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default VaccineDistribution;